import {AttrsSchema, Component} from 'panel';

import {truncateMiddle} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';

import {template} from './template';
import css from './index.inline.styl';

const {TEXT_NODE, ELEMENT_NODE} = document;
const IGNORED_NODES = /^svg$/;
const MAX_LINE_LENGTH = 65;

const indentStr = (numIndents) => `  `.repeat(numIndents);
const t = (kind, text) => ({class: kind, text}); // small helper to create styled tokens

type CodeTipState = {
  open: boolean;
};

type CodeTipAttrs = {
  'target-component': string;
};

type CodeTipJsxProps = {
  attrs?: CodeTipAttrs;
};

export class CodeTip extends Component<CodeTipState, CodeTipAttrs> {
  static get attrsSchema(): AttrsSchema<CodeTipAttrs> {
    return {
      'target-component': {
        type: `string`,
        default: null,
        description: `If specified, code tip will show source code tree down to the element of this name, but not its children.`,
      },
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        open: false,
      },
      helpers: {
        handleCodeTipBtnClick: () => this.update({open: !this.state.open}),
        getJadeParts: () => {
          // we only care about children of code-tip for the source
          return this.elToDomAst(this)
            .children.map((node) => this.domAstToTokens(node))
            .reduce((flattened, tokens) => flattened.concat(tokens), []);
        },
      },
    };
  }

  /** Converts a DOM Element to an abstract simple tree. A simple nested object */
  elToDomAst(el: HTMLElement) {
    if (el.nodeType === TEXT_NODE) {
      const text = el.nodeValue.trim();
      if (text) {
        return {type: TEXT_NODE, text};
      }
    } else if (el.nodeType === ELEMENT_NODE) {
      const name = el.nodeName.toLowerCase();
      return {
        type: ELEMENT_NODE,
        name,
        id: el.id,
        classes: Array.from(el.classList),
        attrs: Array.from(el.attributes)
          .filter((a) => !a.name.match(/^id|class$/))
          .map(({name, value}) => ({name, value})),
        children:
          name === this.attr(`target-component`)
            ? []
            : Array.from(el.childNodes)
                .map((el) => this.elToDomAst(el as HTMLElement))
                .filter(Boolean),
      };
    }
    return null;
  }

  /** Converts a DOM element AST to jade token parts for mp-formatted-message */
  domAstToTokens(node, numIndents = 0) {
    if (node.type === TEXT_NODE) {
      // split into lines of 10 words
      return node.text.match(/(\s*\S+){1,10}/g).map((chunk) => `${indentStr(numIndents)}| ${chunk}\n`);
    } else if (node.type === ELEMENT_NODE) {
      if (node.name.match(IGNORED_NODES)) {
        return [];
      }

      let tokens = [];
      tokens.push(indentStr(numIndents));

      // rather than outputting `div.some-class` or `div#some-id`
      // output jade shorthand i.e `.some-class` or `#some-id`
      if (!(node.name === `div` && (node.id || node.classes.length))) {
        tokens.push(t(`node`, node.name));
      }
      if (node.id) {
        tokens.push(t(`selector`, `#${node.id}`));
      }
      if (node.classes.length) {
        tokens = tokens.concat(node.classes.map((cls) => t(`selector`, `.${cls}`)));
      }
      if (node.attrs.length) {
        tokens.push(t(`sep`, `(`));
        tokens.push(`\n${indentStr(++numIndents)}`);
        tokens.push(t(`key`, `attrs`));
        tokens.push(t(`sep`, `={`));

        const attrTokens = node.attrs
          .map(({name, value}) => {
            // Shorten super long json strings
            value = truncateMiddle(
              value.replace(/\n/g, ``),
              MAX_LINE_LENGTH - name.length - indentStr(numIndents + 1).length,
            );
            return [
              ``, // multiline mode edits empty str to \n<indent>
              t(`key`, name.match(/^\w+$/) ? name : `'${name}'`),
              t(`sep`, `: `),
              value.match(/^(\d+|true|false|null)$/) ? t(`lit`, value) : t(`str`, `\`${value}\``),
              t(`sep`, `, `),
            ];
          })
          .reduce((a, i) => a.concat(i), []);

        tokens = tokens.concat(attrTokens);
        const charCount = tokens.reduce((sum, t) => (sum += typeof t === `string` ? t.length : t.text.length), 0);

        // move attr props to one line each, if inline will get too long
        if (charCount > MAX_LINE_LENGTH) {
          let attrIndex = tokens.length - attrTokens.length;
          const tokenIncrement = 5; // number of spans per attr
          for (; attrIndex < tokens.length; attrIndex += tokenIncrement) {
            tokens[attrIndex] = `\n${indentStr(numIndents + 1)}`;
          }
          tokens.push(`\n${indentStr(numIndents)}`);
        } else {
          tokens.pop(); // remove last dangling comma
        }

        tokens.push(t(`sep`, `}`));
        tokens.push(`\n${indentStr(--numIndents)}`);
        tokens.push(t(`sep`, `)`));
      }

      tokens.push(`\n`);
      return node.children.reduce((tokens, child) => tokens.concat(this.domAstToTokens(child, numIndents + 1)), tokens);
    }
  }
}

export const CodeTipEl = defineAndMakeJsxFactory<CodeTipJsxProps>(`code-tip`, CodeTip);
